import { useEffect, useRef, useState } from "react";
import { api, type MyGalleryEntry } from "./api";
import { People } from "./icons";
import { useFeature } from "./features";
import "./grading.css";
import "./ClassShare.css";

// Share with the class (decision 62, feature "gallery"): the project's owner puts a copy of it in the
// class gallery, with a title and their first name. The server screens it (decision 61): it shows up
// at once, waits for the teacher, or comes back with a kind message about what to change.

const STATUS_TEXT: Record<MyGalleryEntry["status"], string> = {
  live: "It's in the class gallery now. Classmates can play it and remix it.",
  pending: "Your teacher will look at it before your class sees it.",
  hidden: "Your teacher took this one down. Sharing again sends it to your teacher first.",
};

export function ShareToClassButton({ projectId, title, disabled, beforeOpen }: { projectId: string; title: string; disabled: boolean; beforeOpen: () => Promise<void> }) {
  const on = useFeature("gallery");
  const [open, setOpen] = useState(false);
  if (!on) return null;
  return (
    <>
      <button type="button" className="pill-dark" title="Share with class" disabled={disabled} onClick={() => setOpen(true)}>
        <span className="bar-icon"><People size={18} /></span>
        <span className="bar-label">Share with class</span>
      </button>
      {open && <ShareToClassDialog projectId={projectId} title={title} beforeOpen={beforeOpen} onClose={() => setOpen(false)} />}
    </>
  );
}

function ShareToClassDialog({ projectId, title, beforeOpen, onClose }: { projectId: string; title: string; beforeOpen: () => Promise<void>; onClose: () => void }) {
  const [cardTitle, setCardTitle] = useState(title);
  const [byline, setByline] = useState("");
  const [entry, setEntry] = useState<MyGalleryEntry | null>(null);
  const [done, setDone] = useState<"live" | "pending" | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const first = useRef<HTMLInputElement>(null);

  useEffect(() => {
    first.current?.focus();
    api.myGalleryEntry(projectId).then(
      ({ entry: e }) => {
        setEntry(e);
        if (e) {
          setCardTitle(e.title);
          setByline(e.byline);
        }
      },
      () => {},
    );
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [projectId]);

  const share = async () => {
    setBusy(true);
    setError(null);
    try {
      await beforeOpen(); // unsaved edits reach the server first, so the copy is what's on screen
      const r = await api.shareToGallery(projectId, { title: cardTitle, byline });
      setDone(r.status);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="dlg-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="dlg cs-share" role="dialog" aria-modal="true" aria-labelledby="cs-share-title">
        <div className="dlg-head">
          <span className="eyebrow">Share with class</span>
          <h2 id="cs-share-title">{done === "live" ? "Shared!" : done === "pending" ? "Sent to your teacher" : "Put it in the class gallery"}</h2>
        </div>

        {done ? (
          <div className="dlg-body">
            <p className={`cs-status ${done}`} role="status">
              {done === "live" ? STATUS_TEXT.live : "Thanks! Your teacher will look at it before your class sees it. That's normal, so you don't need to do anything."}
            </p>
            <div className="row">
              <button type="button" className="primary small" onClick={onClose}>
                Back to my project
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="dlg-body">
              <p className="muted small">Your classmates can play a copy of your project as it is right now, and remix it. Later changes don't show up until you share again.</p>
              {entry && <p className={`cs-status ${entry.status}`}>{STATUS_TEXT[entry.status]}</p>}
              <label className="share-field">
                Title on the card
                <input ref={first} value={cardTitle} maxLength={60} onChange={(e) => setCardTitle(e.target.value)} />
              </label>
              <label className="share-field">
                Your first name
                <input value={byline} maxLength={30} placeholder="Maya" onChange={(e) => setByline(e.target.value)} />
              </label>
              <p className="muted small">Just your first name. No last names, emails, links or phone numbers.</p>
            </div>
            {error && (
              <p className="dlg-error" role="alert">
                {error}
              </p>
            )}
            <div className="row">
              <button type="button" className="primary small" disabled={busy || !cardTitle.trim() || !byline.trim()} onClick={share}>
                {busy ? "Sharing…" : entry && entry.status !== "hidden" ? "Share the new version" : "Share with my class"}
              </button>
              <button type="button" className="linklike" onClick={onClose}>
                Cancel
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
